/**
 * Cabecera de la tienda: barra de avisos, marca y buscador, y barra de lineas
 * con el panel del catalogo colgando.
 *
 * Es un `<header>` con la navegacion dentro de dos `<nav>` etiquetados: uno para
 * las lineas destacadas y otro para el panel completo del catalogo. Que cada
 * region tenga su nombre es lo que le permite a un lector de pantalla saltar
 * directo a la navegacion en vez de leer la cabecera entera.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import type { LineaNavegacion } from '@elcarpintero/compartido';

import { SITIO } from '../../configuracion/sitio';
import { useDespacho } from '../../contexto/despacho-contexto';
import { useConsultaMedios } from '../../hooks/use-consulta-medios';
import { ofreceDespacho, umbralDespachoGratis } from '../../utilidades/entrega';
import { pesos } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';
import { registrarClicWhatsapp } from '../../servicios/metricas.servicio';
import { whatsappGeneral } from '../../utilidades/whatsapp';
import { Icono } from '../comunes/Icono';
import { Imagen } from '../comunes/Imagen';
import { IndicadorCarro } from '../carro/IndicadorCarro';
import { BotonTema } from './BotonTema';
import { Buscador } from './Buscador';
import { MegaMenu } from './MegaMenu';

/** Cuantas lineas caben en la barra antes de mandar el resto al panel. */
const LINEAS_EN_BARRA = 6;
/* En el telefono la caja es angosta: cualquier frase se cortaba a medias. */
const MARCADOR_BUSCADOR = 'Busca por nombre, código o para qué lo necesitas…';
const MARCADOR_BUSCADOR_CORTO = 'Busca';
const MEDIA_TELEFONO = '(max-width:720px)';

export interface CabeceraProps {
  lineas: LineaNavegacion[];
}

export function Cabecera({ lineas }: CabeceraProps) {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const { opciones: opcionesDespacho } = useDespacho();
  const telefono = useConsultaMedios(MEDIA_TELEFONO);
  const despachoGratisDesde = umbralDespachoGratis(opcionesDespacho);
  // Con el despacho apagado desde el panel, la barra no lo promete.
  const conDespacho = ofreceDespacho(opcionesDespacho);
  const contenedor = useRef<HTMLDivElement>(null);
  const ubicacion = useLocation();

  // Cambiar de pagina cierra el panel: si no, queda tapando la vista nueva.
  useEffect(() => {
    setMenuAbierto(false);
  }, [ubicacion.pathname, ubicacion.search]);

  // El panel del catalogo ocupa TODO lo que queda de pantalla bajo la cabecera
  // (rediseño del 14-09-2026, como el menu de imperial.cl). Para eso el CSS
  // necesita saber cuanto mide la cabecera, y la cabecera cambia de alto con
  // el ancho de pantalla y con el texto de los avisos: se mide y se publica en
  // `--cabecera-alto`, en vez de escribir un numero que dejaria de calzar.
  useEffect(() => {
    const cabecera = contenedor.current;
    if (cabecera === null) return;

    const publicarAlto = (): void => {
      cabecera.style.setProperty('--cabecera-alto', `${cabecera.offsetHeight}px`);
    };
    publicarAlto();
    const observador = new ResizeObserver(publicarAlto);
    observador.observe(cabecera);
    return () => observador.disconnect();
  }, []);

  // Escape y clic fuera cierran el panel, como cualquier menu del sistema.
  // Mientras esta abierto, la pagina de atras no se desplaza: el panel la tapa
  // entera y desplazarla a ciegas solo desorienta.
  useEffect(() => {
    if (!menuAbierto) return;

    function alPresionar(evento: KeyboardEvent): void {
      if (evento.key === 'Escape') setMenuAbierto(false);
    }
    function alHacerClic(evento: MouseEvent): void {
      if (!contenedor.current?.contains(evento.target as Node)) setMenuAbierto(false);
    }

    // Bloquear el desplazamiento saca la barra del navegador, y con ella la
    // pagina se ensancha y todo salta unos pixeles a la derecha. Ese ancho se
    // mide ANTES de bloquear y se publica en `--hueco-barra`: lo usan el
    // cuerpo, el panel y el boton flotante para quedarse donde estaban.
    const raiz = document.documentElement;

    // En el telefono la cabecera no queda fija (`responsive.css`): si la
    // pagina bajo un poco, su borde de abajo ya no esta a `--cabecera-alto`
    // del borde de la pantalla. El panel se cuelga de donde esta de verdad.
    const cabecera = contenedor.current;
    if (cabecera !== null) {
      const borde = Math.max(0, cabecera.getBoundingClientRect().bottom);
      cabecera.style.setProperty('--megamenu-arriba', `${borde}px`);
    }

    const huecoBarra = window.innerWidth - raiz.clientWidth;
    raiz.style.setProperty('--hueco-barra', `${huecoBarra}px`);

    const desplazamientoPrevio = document.body.style.overflow;
    const rellenoPrevio = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${huecoBarra}px`;
    document.addEventListener('keydown', alPresionar);
    document.addEventListener('mousedown', alHacerClic);
    return () => {
      document.body.style.overflow = desplazamientoPrevio;
      document.body.style.paddingRight = rellenoPrevio;
      raiz.style.removeProperty('--hueco-barra');
      document.removeEventListener('keydown', alPresionar);
      document.removeEventListener('mousedown', alHacerClic);
    };
  }, [menuAbierto]);

  const destacadas = lineas.slice(0, LINEAS_EN_BARRA);

  return (
    <header className="cabecera" ref={contenedor}>
      <div className="cabecera-avisos">
        <div className="contenedor">
          <p className="avisos-izq">
            {conDespacho ? (
              <span className="aviso">
                <Icono nombre="camion" />
                Despacho a todo Chile
                {despachoGratisDesde !== null ? ` · gratis sobre ${pesos(despachoGratisDesde)}` : ''}
              </span>
            ) : null}
            <span className="aviso avisos-der">
              <Icono nombre="tienda" />
              Sala de ventas: {SITIO.direccion.calle}, {SITIO.direccion.comuna}
            </span>
          </p>
          <p className="avisos-izq avisos-der">
            <span className="aviso">
              <Icono nombre="reloj" />
              {SITIO.horario}
            </span>
            <a
              className="aviso"
              href={whatsappGeneral()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => registrarClicWhatsapp({ origen: 'cabecera' })}
            >
              <Icono nombre="wa" />
              <b>{SITIO.whatsappVisible}</b>
            </a>
          </p>
        </div>
      </div>

      <div className="cabecera-principal">
        <div className="contenedor">
          <Link className="marca" to={rutas.inicio()}>
            <Imagen
              className="logo-marca"
              src={SITIO.logo}
              alt={`${SITIO.nombreLargo}, ir al inicio`}
              ancho={92}
              alto={46}
              prioridad
            />
          </Link>

          <div className="cabecera-buscador">
            <Buscador
              id="buscador-cabecera"
              marcador={telefono ? MARCADOR_BUSCADOR_CORTO : MARCADOR_BUSCADOR}
              atajoFoto
            />
          </div>

          {/*
            La tienda no enlaza al backoffice. El panel es una herramienta
            interna y hasta diez personas lo usan: un boton permanente en la
            cabecera solo le anuncia al resto de los visitantes que existe. Se
            entra escribiendo /admin/acceso.
          */}
          <div className="acciones">
            <BotonTema />
            <IndicadorCarro />
          </div>
        </div>
      </div>

      <nav className="cabecera-nav" aria-label="Líneas destacadas">
        <div className="contenedor">
          <button
            type="button"
            className={menuAbierto ? 'nav-item abierto' : 'nav-item'}
            aria-expanded={menuAbierto}
            aria-controls="panel-catalogo"
            onClick={() => setMenuAbierto((abierto) => !abierto)}
          >
            <Icono nombre="menu" />
            Todas las líneas
            <Icono nombre="chevron" />
          </button>

          {destacadas.map((linea) => (
            <Link key={linea.id} className="nav-item" to={rutas.linea(linea.slug)}>
              {linea.nombre}
            </Link>
          ))}
        </div>
      </nav>

      {/*
        El panel tapa lo que queda de pantalla: el cuerpo blanco arriba y, si
        sobra alto, un velo oscuro debajo. Un clic en ese velo cierra.
      */}
      <div
        className="megamenu"
        id="panel-catalogo"
        hidden={!menuAbierto}
        onClick={(evento) => {
          if (evento.target === evento.currentTarget) setMenuAbierto(false);
        }}
      >
        <div className="megamenu-cuerpo">
          <nav className="contenedor" aria-label="Catálogo completo">
            {/* La `key` lo vuelve a montar al abrir y al cerrar: asi la linea
                que quedo fija con un clic no sobrevive al cierre del panel. */}
            <MegaMenu
              key={menuAbierto ? 'abierto' : 'cerrado'}
              lineas={lineas}
              prefijo="menu"
              alNavegar={() => setMenuAbierto(false)}
            />
          </nav>
        </div>
      </div>
    </header>
  );
}
